import { inject } from '@angular/core';
import type { CanActivateFn, CanMatchFn, UrlSegment, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { UserApiService } from '@dev-fast/backend-services';
import type { Observable } from 'rxjs';
import { catchError, map, of, take } from 'rxjs';

import { EnvironmentService } from '@app/core/environment-service';
import { IS_SERVER_TOKEN } from '@app/shared/utils';

const normalizeRedirectToAfterLogin = (url: string): string => (url.startsWith('/') ? url : `/${url}`);

const getRedirectToAfterLoginFromSegments = (segments: UrlSegment[]): string => {
  const path = segments.map((segment) => segment.path).join('/');

  return normalizeRedirectToAfterLogin(path);
};

const getRedirectToAfterLoginFromCurrentNavigation = (router: Router, segments: UrlSegment[]): string => {
  const currentNavigation = router.getCurrentNavigation();
  const attemptedUrl = currentNavigation?.extractedUrl ?? currentNavigation?.initialUrl;

  if (attemptedUrl) {
    return normalizeRedirectToAfterLogin(router.serializeUrl(attemptedUrl));
  }

  return getRedirectToAfterLoginFromSegments(segments);
};

const canAccessPrivateSurface = (redirectToAfterLogin: string): Observable<boolean | UrlTree> => {
  const environmentService = inject(EnvironmentService);

  if (!environmentService.isSurfaceAuthWallEnabled()) {
    return of(true);
  }

  const isServer = inject<boolean>(IS_SERVER_TOKEN);
  const router = inject(Router);
  const userApiService = inject(UserApiService);

  return userApiService.getMeProfile({ detailed: false }).pipe(
    take(1),
    map(() => true),
    catchError(() => {
      if (isServer) {
        return of(router.createUrlTree(['/not-found']));
      }

      return of(
        router.createUrlTree(['/login'], {
          queryParams: { redirectToAfterLogin: normalizeRedirectToAfterLogin(redirectToAfterLogin) },
        }),
      );
    }),
  );
};

export const surfaceAuthWallCanActivateGuard: CanActivateFn = (_route, state) => canAccessPrivateSurface(state.url);

export const surfaceAuthWallCanMatchGuard: CanMatchFn = (_route, segments) => {
  const router = inject(Router);

  return canAccessPrivateSurface(getRedirectToAfterLoginFromCurrentNavigation(router, segments));
};
